// WP-3.1 — Consent-gated, provider-agnostic analytics loader (DARK by default).
//
// "Consent precedes analytics" (WP-3 §8 OP3): nothing loads unless analytics
// consent is GRANTED *and* a known provider is configured. Per the WP-3.1 owner
// decision ("none yet — consent only"), the provider registry is empty, so this
// loader is a fully inert scaffold today — adding a provider later (registry entry
// + shim) is the only change needed to activate, and it stays consent-gated.
//
// It owns no identity/content (ADR-0009 consumer) and never runs during prerender
// (consent is never granted in the headless snapshot, and no provider is wired).

import { isAnalyticsConfigured } from './operationalConfig';
import { grantAnalyticsConsentMode } from './gtm';

// provider key -> init({ id }). OPS-4: GTM is the single governed container (GA4 + Clarity are
// configured inside GTM, Owner-side — no direct vendor tags in app code). Consent-gated + dark.
const PROVIDERS = Object.freeze({
  gtm: ({ id }) => grantAnalyticsConsentMode(id),
});

let loaded = false;

// Pure decision: may analytics load? (analyticsConsent: 'unset'|'granted'|'denied')
export function shouldLoadAnalytics({ analyticsConsent, provider, id } = {}) {
  return analyticsConsent === 'granted' && isAnalyticsConfigured({ provider, id });
}

// Idempotent loader. No-op unless allowed AND a provider shim exists.
// Returns the provider key actually loaded, or null (dark / no-op).
export function loadAnalytics({ analyticsConsent, provider, id } = {}) {
  if (loaded) return null;
  if (!shouldLoadAnalytics({ analyticsConsent, provider, id })) return null;
  const init = PROVIDERS[provider];
  if (typeof init !== 'function') return null; // no provider wired yet → dark
  init({ id });
  loaded = true;
  return provider;
}

// test-only reset of the idempotency latch
export function __resetAnalyticsForTests() {
  loaded = false;
}
